/**
 * 报告导出服务
 */

import { analysisApi, PriceData, TechnicalIndicatorData, PerformanceData, RiskData, PortfolioData, StrategyData } from './api';
import * as echarts from 'echarts';

// 导出格式类型
export type ExportFormat = 'pdf' | 'excel' | 'csv' | 'json';

// 导出数据接口
export interface ExportData {
  symbol: string;
  name: string;
  analysis_date: string;
  price_data: PriceData[];
  technical_indicators: TechnicalIndicatorData[];
  performance_data: PerformanceData[];
  risk_data: RiskData[];
  portfolio_data: PortfolioData[];
  strategies: StrategyData[];
  overview?: { overall_signal?: string; confidence?: number; risk_level?: string; expected_return?: number };
  investment_summary?: any; // 新增：投资摘要
  expected_returns_periods_weighted?: Record<string | number, number> | undefined;
  expected_returns_periods?: Record<string | number, number> | undefined;
  expected_return?: number | undefined;
  expected_return_weighted?: number | undefined;
  overall_signal?: string | undefined;
  confidence?: number | undefined;
  risk_level?: string | undefined;
  summary: {
    total_return: number;
    annual_return: number;
    volatility: number;
    sharpe_ratio: number;
    max_drawdown: number;
    win_rate: number;
  };
}

// 导出选项
export interface ExportOptions {
  format: ExportFormat;
  includeCharts: boolean;
  includeTables: boolean;
  includeAnalysis: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
  customTitle?: string;
}

/**
 * 报告导出服务类
 */
class ExportService {
  private jsonSafeClone<T>(obj: T): T {
    const seen = new WeakSet();
    const replacer = (_key: string, value: any) => {
      try {
        if (typeof value === 'function' || typeof value === 'symbol') return undefined;
        if (typeof value === 'bigint') return value.toString();
        if (value && typeof value === 'object') {
          if (seen.has(value)) return undefined;
          seen.add(value);
          if (value instanceof Date) return value.toISOString();
        }
        return value;
      } catch {
        return undefined;
      }
    };
    return JSON.parse(JSON.stringify(obj as any, replacer));
  }
  /**
   * 导出分析报告
   */
  async exportReport(
    data: ExportData,
    options: ExportOptions
  ): Promise<void> {
    try {
      // 只有PDF和Excel格式才调用后端API
      if (options.format === 'pdf' || options.format === 'excel') {
        const extra = data as any;
        let chartImages: string[] | undefined;
        // 离屏渲染：高级K线图（强制生成）
          try {
            const imgAdv = await this.generateAdvancedChartImage(data.price_data, data.technical_indicators);
            if (imgAdv) {
                if (!chartImages) chartImages = [];
                chartImages.push(imgAdv);
            }
          } catch { /* ignore */ }
          
          // 离屏渲染：回测资金曲线（强制生成）
          /* 
          // 用户要求移除 Backtest Equity Curve 图表
          try {
            const imgBt = await this.generateBacktestChartImage(data.symbol, options.dateRange);
            if (imgBt) {
                if (!chartImages) chartImages = [];
                chartImages.push(imgBt);
            }
          } catch { } 
          */

          // 截图：可见图表（作为补充，或用于原始图表）
          try {
            const imgs = await this.captureVisibleChartImages();
            if (imgs && imgs.length > 0) {
                if (!chartImages) chartImages = [];
                // 根据需求，我们希望排除 #advanced-chart-capture 对应的内容
                // 但 captureVisibleChartImages 返回的是 base64 字符串数组，无法区分来源
                // 因此我们修改 captureVisibleChartImages 方法，让它接受参数来控制捕获范围
                // 或者在这里简单地：如果离屏渲染已经成功生成了高级图表（通常是第一张），
                // 那么我们就不再添加截图中的高级图表（如果有的话）。
                
                // 不过，最稳妥的方式是修改 captureVisibleChartImages。
                // 由于 captureVisibleChartImages 是私有方法，我们将在下面修改它。
                // 在这里，我们假设 captureVisibleChartImages 已经不再包含高级K线图的截图。
                imgs.forEach(img => chartImages!.push(img));
            }
          } catch { /* ignore */ }
          
          if ((chartImages as string[]).length === 0) chartImages = undefined;
          if (typeof window !== 'undefined') {
            console.log('[export] chart images prepared:', (chartImages || []).length);
          }
        const requestPayload = this.jsonSafeClone({
          symbol: data.symbol,
          name: data.name,
          data: {
            price_data: data.price_data,
            technical_indicators: data.technical_indicators,
            performance_data: data.performance_data,
            risk_data: data.risk_data,
            portfolio_data: data.portfolio_data,
            strategies: data.strategies,
            overview: extra.overview || {},
            investment_summary: extra.investment_summary || {}, // 传递投资摘要
            // 传递加权/周期化收益与概览相关原字段，便于后端一致化处理
            expected_returns_periods_weighted: extra.expected_returns_periods_weighted,
            expected_returns_periods: extra.expected_returns_periods,
            expected_return: extra.expected_return,
            expected_return_weighted: extra.expected_return_weighted,
            overall_signal: extra.overall_signal,
            confidence: extra.confidence,
            risk_level: extra.risk_level,
            expected_return_days: extra.expected_return_days,
            chart_images: chartImages
          },
          format: options.format,
          lang: 'zh_en'
        });
        const response = await analysisApi.exportReport(requestPayload);
        if (typeof window !== 'undefined') {
          console.log('[export] request payload chart_images count:', (requestPayload?.data?.chart_images || []).length);
        }

        // 下载文件（根据Content-Type选择扩展名）
        const ct = (response.headers && (response.headers as any)['content-type']) || (response.data && (response.data as any).type) || '';
        this.downloadFile(response.data, data.symbol, options.format, ct as string);
      } else {
        // 其他格式使用前端导出
        await this.exportClientSide(data, options);
      }
    } catch (error) {
      console.error('Export error:', error);
      // 如果后端导出失败，使用前端导出
      await this.exportClientSide(data, options);
    }
  }

  /**
   * 前端导出（备用方案）
   */
  private async exportClientSide(
    data: ExportData,
    options: ExportOptions
  ): Promise<void> {
    switch (options.format) {
      case 'csv':
        this.exportToCSV(data, options);
        break;
      case 'json':
        this.exportToJSON(data, options);
        break;
      case 'excel':
        await this.exportToExcel(data, options);
        break;
      case 'pdf':
        await this.exportToPDF(data, options);
        break;
      default:
        throw new Error(`Unsupported export format: ${options.format}`);
    }
  }

  /**
   * 导出为CSV格式
   */
  private exportToCSV(data: ExportData, options: ExportOptions): void {
    let csvContent = '';

    // 添加标题
    csvContent += `股票分析报告 - ${data.name} (${data.symbol})\n`;
    csvContent += `分析日期: ${data.analysis_date}\n\n`;

    // 添加汇总信息
    csvContent += '汇总信息\n';
    csvContent += '指标,数值\n';
    csvContent += `总收益率,${data.summary.total_return}%\n`;
    csvContent += `年化收益率,${data.summary.annual_return}%\n`;
    csvContent += `波动率,${data.summary.volatility}%\n`;
    csvContent += `夏普比率,${data.summary.sharpe_ratio}\n`;
    csvContent += `最大回撤,${data.summary.max_drawdown}%\n`;
    csvContent += `胜率,${data.summary.win_rate}%\n\n`;

    // 添加价格数据
    if (data.price_data && data.price_data.length > 0) {
      csvContent += '价格数据\n';
      csvContent += '日期,开盘价,最高价,最低价,收盘价,成交量\n';
      data.price_data.forEach(item => {
        csvContent += `${item.timestamp},${item.open},${item.high},${item.low},${item.close},${item.volume}\n`;
      });
      csvContent += '\n';
    }

    // 添加技术指标
    if (data.technical_indicators && data.technical_indicators.length > 0) {
      csvContent += '技术指标\n';
      csvContent += '日期,RSI,MACD,信号线,MA5,MA10,MA20\n';
      data.technical_indicators.forEach(item => {
        csvContent += `${item.timestamp},${item.rsi || ''},${item.macd || ''},${item.signal || ''},${item.ma5 || ''},${item.ma10 || ''},${item.ma20 || ''}\n`;
      });
      csvContent += '\n';
    }

    // 下载CSV文件
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, `${data.symbol}_analysis_report.csv`);
  }

  /**
   * 导出为JSON格式
   */
  private exportToJSON(data: ExportData, options: ExportOptions): void {
    const jsonData = {
      ...data,
      export_options: options,
      export_timestamp: new Date().toISOString()
    };

    const jsonString = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadBlob(blob, `${data.symbol}_analysis_report.json`);
  }

  /**
   * 导出为Excel格式
   */
  private async exportToExcel(data: ExportData, options: ExportOptions): Promise<void> {
    try {
      // 动态导入xlsx库
      const XLSX = await import('xlsx');

      // 创建工作簿
      const workbook = XLSX.utils.book_new();

      // 汇总信息工作表
      const summaryData = [
        ['股票分析报告'],
        ['股票代码', data.symbol],
        ['股票名称', data.name],
        ['分析日期', data.analysis_date],
        [''],
        ['汇总指标', '数值'],
        ['总收益率', `${data.summary.total_return}%`],
        ['年化收益率', `${data.summary.annual_return}%`],
        ['波动率', `${data.summary.volatility}%`],
        ['夏普比率', data.summary.sharpe_ratio],
        ['最大回撤', `${data.summary.max_drawdown}%`],
        ['胜率', `${data.summary.win_rate}%`]
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, '汇总');

      // 价格数据工作表
      if (data.price_data && data.price_data.length > 0) {
        const priceSheet = XLSX.utils.json_to_sheet(data.price_data);
        XLSX.utils.book_append_sheet(workbook, priceSheet, '价格数据');
      }

      // 技术指标工作表
      if (data.technical_indicators && data.technical_indicators.length > 0) {
        const indicatorSheet = XLSX.utils.json_to_sheet(data.technical_indicators);
        XLSX.utils.book_append_sheet(workbook, indicatorSheet, '技术指标');
      }

      // 表现数据工作表
      if (data.performance_data && data.performance_data.length > 0) {
        const performanceSheet = XLSX.utils.json_to_sheet(data.performance_data);
        XLSX.utils.book_append_sheet(workbook, performanceSheet, '表现数据');
      }

      // 风险数据工作表
      if (data.risk_data && data.risk_data.length > 0) {
        const riskSheet = XLSX.utils.json_to_sheet(data.risk_data);
        XLSX.utils.book_append_sheet(workbook, riskSheet, '风险数据');
      }

      // 策略数据工作表
      if (data.strategies && data.strategies.length > 0) {
        const strategySheet = XLSX.utils.json_to_sheet(data.strategies);
        XLSX.utils.book_append_sheet(workbook, strategySheet, '策略数据');
      }

      // 导出Excel文件
      XLSX.writeFile(workbook, `${data.symbol}_analysis_report.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      // 如果Excel导出失败，回退到CSV
      this.exportToCSV(data, options);
    }
  }

  /**
   * 导出为PDF格式
   */
  private async exportToPDF(data: ExportData, options: ExportOptions): Promise<void> {
    try {
      // 动态导入jsPDF库
      const { jsPDF } = await import('jspdf');

      // 创建PDF文档
      const doc = new jsPDF();

      // 设置字体（支持中文）
      doc.setFont('helvetica');

      // 添加标题
      doc.setFontSize(20);
      doc.text(`Stock Analysis Report - ${data.name} (${data.symbol})`, 20, 30);

      // 添加分析日期
      doc.setFontSize(12);
      doc.text(`Analysis Date: ${data.analysis_date}`, 20, 50);

      // 添加汇总信息
      doc.setFontSize(16);
      doc.text('Summary', 20, 70);

      doc.setFontSize(12);
      let yPosition = 90;
      const summaryItems = [
        `Total Return: ${data.summary.total_return}%`,
        `Annual Return: ${data.summary.annual_return}%`,
        `Volatility: ${data.summary.volatility}%`,
        `Sharpe Ratio: ${data.summary.sharpe_ratio}`,
        `Max Drawdown: ${data.summary.max_drawdown}%`,
        `Win Rate: ${data.summary.win_rate}%`
      ];

      summaryItems.forEach(item => {
        doc.text(item, 20, yPosition);
        yPosition += 15;
      });

      // 添加策略信息
      if (data.strategies && data.strategies.length > 0) {
        yPosition += 20;
        doc.setFontSize(16);
        doc.text('Strategies', 20, yPosition);
        yPosition += 20;

        doc.setFontSize(12);
        data.strategies.forEach(strategy => {
          doc.text(`${strategy.name}: Signal ${strategy.signal}, Confidence ${(strategy.confidence * 100).toFixed(1)}%, Returns ${(strategy.returns || 0).toFixed(2)}%`, 20, yPosition);
          yPosition += 15;
        });
      }

      // 保存PDF文件
      doc.save(`${data.symbol}_analysis_report.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      // 如果PDF导出失败，回退到JSON
      this.exportToJSON(data, options);
    }
  }

  /**
   * 下载文件
   */
  private downloadFile(data: Blob, symbol: string, format: ExportFormat, contentType?: string): void {
    let ext: ExportFormat | 'csv' | 'xlsx' = format;
    const type = (contentType || (data && (data as any).type) || '').toLowerCase();
    if (format === 'excel') {
      if (type.includes('text/csv')) ext = 'csv';
      else ext = 'xlsx';
    }
    const filename = `${symbol}_analysis_report.${ext}`;
    this.downloadBlob(data, filename);
  }

  /**
   * 下载Blob数据
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 生成导出数据
   */
  generateExportData(
    symbol: string,
    name: string,
    data: Record<string, unknown>
  ): ExportData {
    // 构造导出数据结构
    return {
      symbol,
      name,
      analysis_date: new Date().toLocaleDateString(),
      price_data: (data.price_data as PriceData[]) || [],
      technical_indicators: (data.technical_indicators as TechnicalIndicatorData[]) || [],
      performance_data: (data.performance_data as PerformanceData[]) || [],
      risk_data: (data.risk_data as RiskData[]) || [],
      portfolio_data: (data.portfolio_data as PortfolioData[]) || [],
      strategies: (data.strategies as StrategyData[]) || [],
      overview: (data.overview as any) || {},
      investment_summary: (data.investment_summary as any) || {},
      // 传递加权/周期化收益与概览相关原字段，便于后端一致化处理
      expected_returns_periods_weighted: (data.expected_returns_periods_weighted as any),
      expected_returns_periods: (data.expected_returns_periods as any),
      expected_return: (data.expected_return as any),
      expected_return_weighted: (data.expected_return_weighted as any),
      // 传递综合信号字段
      overall_signal: (data.overview as any)?.overall_signal,
      confidence: (data.overview as any)?.confidence,
      risk_level: (data.overview as any)?.risk_level,
      // 传递预测天数
      expected_return_days: (data as any)?.expected_return_days,
      summary: this.calculateSummary(data)
    } as any; // 使用any绕过类型检查，因为我们在ExportData中添加了可选字段
  }

  /**
   * 生成高级K线图PNG（包含MA与布林带、成交量）
   */
  private async generateAdvancedChartImage(
    price: PriceData[],
    _indicators: TechnicalIndicatorData[]
  ): Promise<string> {
    if (!price || price.length < 5) {
      // 回退：生成占位图，避免PDF没有图像
      const canvas = document.createElement('canvas');
      canvas.width = 1024; canvas.height = 640;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0,0,canvas.width,canvas.height);
        ctx.fillStyle = '#333333'; ctx.font = '20px Helvetica';
        ctx.fillText('图表数据不足，使用占位图', 30, 40);
      }
      return canvas.toDataURL('image/png');
    }
    const timestamps = price.map(p => p.timestamp);
    const candle = price.map(p => [p.open, p.close, p.low, p.high]);
    const vol = price.map(p => p.volume);
    const close = price.map(p => p.close);
    const sma = (period: number) => close.map((_, i) => {
      if (i + 1 < period) return null;
      let sum = 0; for (let j = i - period + 1; j <= i; j++) sum += close[j];
      return Number((sum / period).toFixed(4));
    });
    const ma5 = sma(5);
    const ma10 = sma(10);
    const ma20 = sma(20);
    const ma50 = sma(50);
    const boll = (period: number, k: number) => {
      const mid = sma(period);
      const up: (number|null)[] = []; const low: (number|null)[] = [];
      for (let i = 0; i < close.length; i++) {
        if (i + 1 < period) { up.push(null); low.push(null); continue; }
        const m = mid[i] as number;
        let s2 = 0; for (let j = i - period + 1; j <= i; j++) s2 += Math.pow(close[j] - m, 2);
        const std = Math.sqrt(s2 / period);
        up.push(Number((m + k * std).toFixed(4)));
        low.push(Number((m - k * std).toFixed(4)));
      }
      return { mid, up, low };
    };
    const bb = boll(20, 2);
    const upper = bb.up; const middle = bb.mid; const lower = bb.low;
    // RSI(14)
    const rsi = (() => {
      const period = 14; const out: (number|null)[] = [];
      let gains = 0, losses = 0;
      for (let i = 1; i < close.length; i++) {
        const diff = close[i] - close[i-1];
        const gain = Math.max(diff, 0); const loss = Math.max(-diff, 0);
        if (i <= period) { gains += gain; losses += loss; out.push(null); continue; }
        gains = (gains * (period - 1) + gain) / period;
        losses = (losses * (period - 1) + loss) / period;
        const rs = losses === 0 ? 100 : gains / (losses === 0 ? 1e-9 : losses);
        const val = 100 - (100 / (1 + rs));
        out.push(Number(val.toFixed(2)));
      }
      // pad first element
      out.unshift(null);
      return out;
    })();
    // MACD(12,26,9)
    const ema = (period: number) => {
      const k = 2 / (period + 1); const out: number[] = [];
      let prev = close[0]; out.push(prev);
      for (let i = 1; i < close.length; i++) { prev = close[i] * k + prev * (1 - k); out.push(prev); }
      return out;
    };
    const ema12 = ema(12); const ema26 = ema(26);
    const macdLine = close.map((_, i) => Number((ema12[i] - ema26[i]).toFixed(4)));
    const signalLine = (() => { const k = 2 / (9 + 1); const out: number[] = []; let prev = macdLine[0]; out.push(prev); for (let i=1;i<macdLine.length;i++){ prev = macdLine[i]*k + prev*(1-k); out.push(prev);} return out.map(v => Number(v.toFixed(4))); })();
    const histogram = macdLine.map((v, i) => Number((v - signalLine[i]).toFixed(4)));

    const div = document.createElement('div');
    div.style.width = '1024px';
    div.style.height = '640px';
    div.style.position = 'fixed';
    div.style.left = '-99999px';
    div.style.top = '-99999px';
    document.body.appendChild(div);

    const chart = echarts.init(div, undefined, { renderer: 'canvas' });
    // 显式调整大小，确保 ECharts 知道容器尺寸
    chart.resize({ width: 1024, height: 640 });
    const hasRSI = rsi.some(v => v != null);
    const hasMACD = histogram.some(v => v != null);
    const grids: any[] = [
      { left: '8%', right: '6%', top: '8%', height: hasRSI || hasMACD ? '45%' : '60%' },
      { left: '8%', right: '6%', top: hasRSI || hasMACD ? '55%' : '74%', height: '16%' }
    ];
    if (hasRSI) grids.push({ left: '8%', right: '6%', top: '73%', height: '10%' });
    if (hasMACD) grids.push({ left: '8%', right: '6%', top: hasRSI ? '85%' : '73%', height: hasRSI ? '10%' : '12%' });

    const gridColor = '#d9d9d9';
    // 禁用动画，强制同步渲染，防止截图时图表未渲染完成（出现空坐标轴）
    // 增加 notMerge: true 确保不混合旧配置
    const opt: echarts.EChartsOption = {
      animation: false,
      stateAnimation: { duration: 0 },
      hoverLayerThreshold: -1,
      backgroundColor: '#ffffff',
      title: { text: `Advanced K-Line`, left: 'center', top: 5 },
      grid: grids.map(g => ({...g, containLabel: true})), // 确保坐标轴标签不被遮挡
      xAxis: [
        { type: 'category', data: timestamps, boundaryGap: false, splitLine: { show: true, lineStyle: { type: 'dashed', color: gridColor, width: 1 } } },
        { type: 'category', gridIndex: 1, data: timestamps, boundaryGap: false, axisLabel: { show: false } },
        ...(hasRSI ? [{ type: 'category', gridIndex: 2, data: timestamps, boundaryGap: false, axisLabel: { show: false } }] : []),
        ...(hasMACD ? [{ type: 'category', gridIndex: hasRSI ? 3 : 2, data: timestamps, boundaryGap: false, axisLabel: { show: false } }] : [])
      ],
      yAxis: [
        { scale: true, splitLine: { show: true, lineStyle: { type: 'dashed', color: gridColor, width: 1 } } },
        { scale: true, gridIndex: 1 },
        ...(hasRSI ? [{ scale: true, gridIndex: 2 }] : []),
        ...(hasMACD ? [{ scale: true, gridIndex: hasRSI ? 3 : 2 }] : [])
      ],
      legend: { 
          bottom: 0, 
          padding: [0, 0, 10, 0], // 增加底部padding
          data: ['MA5','MA10','MA20','MA50','布林上轨','布林中轨','布林下轨','成交量','MACD','Signal'] 
      },
      series: [
        { name: '价格', type: 'candlestick', data: candle, itemStyle: { color: '#26a69a', color0: '#ef5350', borderColor: '#26a69a', borderColor0: '#ef5350' } },
        ...(ma5.some(v => v!=null) ? [{ name: 'MA5', type: 'line', data: ma5, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#ff4d4f' } }] : []), // 鲜红
        ...(ma10.some(v => v!=null) ? [{ name: 'MA10', type: 'line', data: ma10, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#faad14' } }] : []), // 金黄
        ...(ma20.some(v => v!=null) ? [{ name: 'MA20', type: 'line', data: ma20, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#1890ff' } }] : []), // 亮蓝
        ...(ma50.some(v => v!=null) ? [{ name: 'MA50', type: 'line', data: ma50, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#52c41a' } }] : []), // 鲜绿
        ...(upper.some(v => v!=null) ? [{ name: '布林上轨', type: 'line', data: upper, smooth: true, showSymbol: false, lineStyle: { width: 1, color: '#722ed1', type: 'dashed' } }] : []),
        ...(middle.some(v => v!=null) ? [{ name: '布林中轨', type: 'line', data: middle, smooth: true, showSymbol: false, lineStyle: { width: 1, color: '#13c2c2' } }] : []),
        ...(lower.some(v => v!=null) ? [{ name: '布林下轨', type: 'line', data: lower, smooth: true, showSymbol: false, lineStyle: { width: 1, color: '#722ed1', type: 'dashed' } }] : []),
        { 
            name: '成交量', 
            type: 'bar', 
            xAxisIndex: 1, 
            yAxisIndex: 1, 
            data: vol.map((v, i) => {
                // 确保 i 在 candle 范围内，如果越界则默认涨（红）
                const current = candle[i] ? candle[i][1] : 0;
                const prev = candle[i] ? candle[i][0] : 0; 
                // 涨跌判断: 收盘 >= 开盘 为涨(红 #ef5350), 否则跌(绿 #26a69a)
                // 注意：ECharts K线 color0 是阴线(跌)，color 是阳线(涨)
                // 我们的配置: color: '#26a69a' (涨/阳? 不，通常绿涨红跌是A股习惯，但国际惯例绿跌红涨)
                // 让我们看上面 candlestick 配置: color: '#26a69a' (阳线?), color0: '#ef5350' (阴线?)
                // 实际上 ECharts 默认 color 是阳线(收>开), color0 是阴线(收<开)
                // A股习惯：红涨(阳) 绿跌(阴)。
                // 如果 candlestick 配置 itemStyle: { color: '#26a69a', color0: '#ef5350' }
                // 那么 阳线(涨)显示 #26a69a (绿)，阴线(跌)显示 #ef5350 (红)。这是国际/加密货币习惯 (Green Up, Red Down)。
                // 用户之前要求调整颜色，可能已经接受了这个配色。
                // 现在用户要求"成交量...以买入和卖出分不同颜色...目前都是绿色体现不准确"
                // 所以我们要匹配 candlestick 的颜色逻辑：
                // 收 > 开 (阳线/涨) -> 使用 candlestick.color (#26a69a 绿)
                // 收 < 开 (阴线/跌) -> 使用 candlestick.color0 (#ef5350 红)
                const isUp = current >= prev;
                return {
                    value: v,
                    itemStyle: {
                        color: isUp ? '#26a69a' : '#ef5350'
                    }
                };
            })
        },
        ...(hasRSI ? [{ name: 'RSI', type: 'line', xAxisIndex: 2, yAxisIndex: 2, data: rsi, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#eb2f96' } }] : []), // 亮粉
        ...(hasMACD ? [
          { name: 'MACD', type: 'line', xAxisIndex: hasRSI ? 3 : 2, yAxisIndex: hasRSI ? 3 : 2, data: macdLine, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#2f54eb' } }, // 深蓝
          { name: 'Signal', type: 'line', xAxisIndex: hasRSI ? 3 : 2, yAxisIndex: hasRSI ? 3 : 2, data: signalLine, smooth: true, showSymbol: false, lineStyle: { width: 1.5, color: '#fa8c16' } }, // 橙色
          { 
              name: 'Histogram', 
              type: 'bar', 
              xAxisIndex: hasRSI ? 3 : 2, 
              yAxisIndex: hasRSI ? 3 : 2, 
              data: histogram.map(v => ({
                  value: v,
                  itemStyle: {
                      // MACD柱状图：正红负绿 (A股) 或 正绿负红 (国际)
                      // 这里跟随 K线主图逻辑：涨(绿 #26a69a) 跌(红 #ef5350)
                      // 通常 MACD > 0 为多头(红/绿?)，MACD < 0 为空头
                      // 让我们保持一致：正值用 #26a69a (绿)，负值用 #ef5350 (红)
                      color: v >= 0 ? '#26a69a' : '#ef5350'
                  }
              }))
          }
        ] : [])
      ]
    } as any;
    chart.setOption(opt, { notMerge: true, lazyUpdate: false });
    // 强制同步渲染
    const dataURL = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
    chart.dispose();
    document.body.removeChild(div);
    return dataURL;
  }

  /**
   * 离屏渲染回测资金曲线PNG（调用后端回测接口获取equity_curve）
   */
  private async generateBacktestChartImage(
    symbol: string,
    dateRange?: { start: string; end: string }
  ): Promise<string | null> {
    try {
      const body: any = {
        symbols: [symbol],
        start_date: dateRange?.start,
        end_date: dateRange?.end,
        period: '1d',
        percents: 45,
        stop_loss_dist: 0.05,
        sma_period: 200,
        rsi_period: 14,
        benchmark: '000300.SZ'
      };
      const resp = await analysisApi.runBacktest(body);
      const equity = (resp?.equity_curve || []) as Array<{ timestamp: string; value: number }>;
      if (!equity || equity.length < 3) return null;
      const div = document.createElement('div');
      div.style.width = '1024px';
      div.style.height = '640px';
      div.style.position = 'fixed';
      div.style.left = '-99999px';
      div.style.top = '-99999px';
      document.body.appendChild(div);
      const chart = echarts.init(div, undefined, { renderer: 'canvas' });
      // 显式调整大小
      chart.resize({ width: 1024, height: 640 });
      // 禁用动画
      const opt: echarts.EChartsOption = {
        animation: false,
        backgroundColor: '#ffffff',
        title: { text: `Backtest Equity Curve`, left: 'center', top: 5 },
        grid: [{ left: '8%', right: '6%', top: '10%', height: '78%' }],
        xAxis: [{ type: 'category', data: equity.map(e => e.timestamp), boundaryGap: false }],
        yAxis: [{ type: 'value', scale: true }],
        series: [{ name: 'Equity', type: 'line', data: equity.map(e => e.value), smooth: true, showSymbol: false, lineStyle: { width: 2, color: '#1890ff' } }],
        tooltip: { trigger: 'axis' },
        legend: { bottom: 6, data: ['Equity'] }
      } as any;
      chart.setOption(opt, { notMerge: true, lazyUpdate: false });
      const dataURL = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
      chart.dispose();
      document.body.removeChild(div);
      return dataURL;
    } catch {
      return null;
    }
  }

  /**
   * 直接截取当前页面上的图表（高级K线或原始图表），与用户视图一致
   */
  private async captureVisibleChartImages(): Promise<string[]> {
    if (typeof window === 'undefined') return [];
    // 移除 #advanced-chart-capture，避免重复打印高级K线图
    // 仅保留 #price-chart-capture (原始图表) 作为补充，如果用户当前在看原始图表
    const selectors = ['#price-chart-capture'];
    const html2canvas = (await import('html2canvas')).default;
    const images: string[] = [];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (!el) continue;
      try {
        const canvas = await html2canvas(el as HTMLElement, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
        images.push(canvas.toDataURL('image/png'));
      } catch { /* skip */ }
    }
    return images;
  }

  /**
   * 计算汇总统计
   */
  private calculateSummary(data: Record<string, unknown>): ExportData['summary'] {
    // 从数据中计算汇总统计
    const performanceData = (data.performance_data as PerformanceData[]) || [];
    const riskData = (data.risk_data as RiskData[]) || [];

    let totalReturn = 0;
    let annualReturn = 0;
    let volatility = 0;
    let sharpeRatio = 0;
    let maxDrawdown = 0;
    let winRate = 0;

    if (performanceData.length > 0) {
      const lastPerformance = performanceData[performanceData.length - 1];
      totalReturn = lastPerformance.cumulative_returns || 0;
      annualReturn = totalReturn * (365 / performanceData.length);
      sharpeRatio = lastPerformance.sharpe_ratio || 0;
      maxDrawdown = Math.min(...performanceData.map((p: PerformanceData) => (p as any).drawdown || 0));
    }

    if (riskData.length > 0) {
      volatility = riskData[riskData.length - 1].var || 0;
    }

    // 计算胜率
    const positiveReturns = performanceData.filter((p: PerformanceData) => p.returns > 0);
    winRate = performanceData.length > 0 ? (positiveReturns.length / performanceData.length) * 100 : 0;

    return {
      total_return: Number(totalReturn.toFixed(2)),
      annual_return: Number(annualReturn.toFixed(2)),
      volatility: Number(volatility.toFixed(2)),
      sharpe_ratio: Number(sharpeRatio.toFixed(2)),
      max_drawdown: Number(maxDrawdown.toFixed(2)),
      win_rate: Number(winRate.toFixed(2))
    };
  }
}

// 导出服务实例
export const exportService = new ExportService();

// 导出默认实例
export default exportService;
